"use client";

import { useTranslations } from "next-intl";
import type { ResearchStudy } from "@/lib/research/types";
import { PeptideRefLink } from "@/ui/components/research/peptide-ref-link";
import { getReferencePeptideBySlug } from "@/lib/research/peptides-reference";

const RCT_TYPES = new Set(["rct-1", "rct-2", "rct-3", "rct-4"]);

interface StudyCardProps {
	study: ResearchStudy;
	locale: string;
}

export function StudyCard({ study, locale }: StudyCardProps) {
	const t = useTranslations("research");
	const isClinical = RCT_TYPES.has(study.type);

	return (
		<article className="hover:border-primary/30 group relative rounded-2xl border border-border bg-card p-5 transition-[border-color,box-shadow] hover:shadow-lg sm:p-6 lg:p-7">
			<div className="relative grid items-start gap-5 lg:grid-cols-[1fr_auto] lg:gap-8">
				<div>
					<div className="flex flex-wrap items-center gap-2">
						<span
							className={`inline-flex items-center rounded-sm px-2 py-0.5 font-mono text-[0.56rem] uppercase tracking-[0.18em] ${
								isClinical ? "bg-primary text-primary-foreground" : "bg-muted text-primary"
							}`}
						>
							{t(`studyTypes.${study.type}`)}
						</span>
						{study.human && (
							<span className="inline-flex items-center rounded-sm border border-success px-1.5 py-0.5 font-mono text-[0.54rem] uppercase tracking-[0.18em] text-success">
								{t("journal.humanBadge")}
							</span>
						)}
						{study.fields.map((field) => (
							<span
								key={field}
								className="inline-flex items-center rounded-sm border border-border px-1.5 py-0.5 font-mono text-[0.54rem] uppercase tracking-[0.14em] text-muted-foreground"
							>
								{t(`studyFields.${field}`)}
							</span>
						))}
					</div>

					<h2 className="mt-3 font-display text-[1.18rem] font-medium leading-[1.2] tracking-tight sm:text-[1.28rem]">
						{study.title}
					</h2>

					<div className="mt-2 text-[0.84rem] leading-snug text-muted-foreground">
						<span className="font-medium text-foreground">{study.authors}</span>
						<span className="text-muted-foreground/60"> · </span>
						<span className="italic">{study.journal}</span>
						<span className="text-muted-foreground/60"> · </span>
						<span className="font-mono">{study.year}</span>
						{study.volume && (
							<>
								<span className="text-muted-foreground/60"> · </span>
								<span className="font-mono text-muted-foreground">{study.volume}</span>
							</>
						)}
					</div>

					<p className="mt-4 text-[0.92rem] leading-relaxed text-foreground">
						{t(`studies.${study.id}.finding`)}
					</p>

					{study.peptideSlugs.length > 0 && (
						<div className="mt-4 flex flex-wrap items-center gap-2 border-t border-border pt-4">
							{study.peptideSlugs.map((slug) => {
								const ref = getReferencePeptideBySlug(slug);
								return (
									<PeptideRefLink
										key={slug}
										slug={slug}
										displayName={ref?.name ?? slug}
										locale={locale}
										className="bg-muted/50 inline-flex items-center rounded-full border border-border px-2.5 py-0.5 font-mono text-[0.58rem] uppercase tracking-[0.14em] hover:border-primary"
									/>
								);
							})}
						</div>
					)}
				</div>

				<div className="flex items-start gap-4 lg:w-44 lg:flex-col lg:border-l lg:border-border lg:pl-6">
					{study.participants != null && (
						<div>
							<div className="font-mono text-[0.54rem] uppercase tracking-[0.18em] text-muted-foreground">
								{t("journal.participantsLabel")}
							</div>
							<div className="mt-0.5 font-mono text-[1.1rem] font-medium tabular-nums">
								{study.participants.toLocaleString(locale)}
							</div>
						</div>
					)}
					{study.phase && (
						<div>
							<div className="font-mono text-[0.54rem] uppercase tracking-[0.18em] text-muted-foreground">
								{t("journal.phaseLabel")}
							</div>
							<div className="mt-0.5 font-mono text-[1.1rem] font-medium">{study.phase}</div>
						</div>
					)}
					{study.doi && (
						<a
							href={`https://doi.org/${study.doi}`}
							target="_blank"
							rel="noopener noreferrer"
							className="hover:text-primary/80 break-all font-mono text-[0.58rem] uppercase tracking-[0.18em] text-primary transition-colors lg:mt-auto"
						>
							{t("journal.doiLink")} · {study.doi}
							<span aria-hidden className="ml-1">
								↗
							</span>
						</a>
					)}
				</div>
			</div>
		</article>
	);
}
